import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { serviceZoneService } from '../../../services/serviceZoneService';

const ServiceZoneManager = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    postalCode: '',
    municipalityName: '',
    latitude: '',
    longitude: '',
    radiusKm: '5.0',
    priority: '1',
    isActive: true,
    notes: ''
  });

  useEffect(() => {
    loadZones();
  }, []);

  const loadZones = async () => {
    try {
      setLoading(true);
      const data = await serviceZoneService?.getAllZones();
      setZones(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Erreur lors du chargement des zones');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      postalCode: '',
      municipalityName: '',
      latitude: '',
      longitude: '',
      radiusKm: '5.0',
      priority: '1',
      isActive: true,
      notes: ''
    });
    setIsCreating(false);
    setEditingZone(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.name?.trim()) {
      setError('Le nom de la zone est requis');
      return;
    }

    if (!formData?.postalCode && !formData?.municipalityName && (!formData?.latitude || !formData?.longitude)) {
      setError('Vous devez fournir au moins un code postal, un nom de commune, ou des coordonnées GPS');
      return;
    }

    try {
      setLoading(true);
      const zoneData = {
        name: formData?.name,
        postalCode: formData?.postalCode || null,
        municipalityName: formData?.municipalityName || null,
        latitude: formData?.latitude ? parseFloat(formData?.latitude) : null,
        longitude: formData?.longitude ? parseFloat(formData?.longitude) : null,
        radiusKm: parseFloat(formData?.radiusKm),
        priority: parseInt(formData?.priority),
        isActive: formData?.isActive,
        notes: formData?.notes || null
      };

      if (editingZone) {
        await serviceZoneService?.updateZone(editingZone?.id, zoneData);
      } else {
        await serviceZoneService?.createZone(zoneData);
      }

      await loadZones();
      resetForm();
      setError('');
    } catch (err) {
      setError(err?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (zone) => {
    setFormData({
      name: zone?.name,
      postalCode: zone?.postalCode || '',
      municipalityName: zone?.municipalityName || '',
      latitude: zone?.latitude?.toString() || '',
      longitude: zone?.longitude?.toString() || '',
      radiusKm: zone?.radiusKm?.toString() || '5.0',
      priority: zone?.priority?.toString() || '1',
      isActive: zone?.isActive,
      notes: zone?.notes || ''
    });
    setEditingZone(zone);
    setIsCreating(true);
  };

  const handleDelete = async (zoneId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette zone ?')) return;

    try {
      setLoading(true);
      await serviceZoneService?.deleteZone(zoneId);
      await loadZones();
      setError('');
    } catch (err) {
      setError(err?.message || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (zoneId, currentStatus) => {
    try {
      await serviceZoneService?.toggleZoneStatus(zoneId, !currentStatus);
      await loadZones();
    } catch (err) {
      setError(err?.message || 'Erreur lors du changement de statut');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Zones d'intervention</h2>
          <p className="text-muted-foreground mt-1">Gérez les zones de service configurables</p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          onClick={() => setIsCreating(!isCreating)}
        >
          {isCreating ? 'Annuler' : 'Nouvelle zone'}
        </Button>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center gap-3">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" />
          <span className="text-sm text-error">{error}</span>
        </div>
      )}

      {isCreating && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {editingZone ? 'Modifier la zone' : 'Créer une nouvelle zone'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Nom de la zone"
              type="text"
              placeholder="Paris 1er"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              required
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Input
                label="Code postal"
                type="text"
                placeholder="75001"
                value={formData?.postalCode}
                onChange={(e) => handleInputChange('postalCode', e?.target?.value)}
                maxLength={5}
              />

              <Input
                label="Nom de la commune"
                type="text"
                placeholder="Paris"
                value={formData?.municipalityName}
                onChange={(e) => handleInputChange('municipalityName', e?.target?.value)}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Input
                label="Latitude"
                type="number"
                step="0.000001"
                placeholder="48.8566"
                value={formData?.latitude}
                onChange={(e) => handleInputChange('latitude', e?.target?.value)}
              />

              <Input
                label="Longitude"
                type="number"
                step="0.000001"
                placeholder="2.3522"
                value={formData?.longitude}
                onChange={(e) => handleInputChange('longitude', e?.target?.value)}
              />

              <Input
                label="Rayon (km)"
                type="number"
                step="0.1"
                placeholder="5.0"
                value={formData?.radiusKm}
                onChange={(e) => handleInputChange('radiusKm', e?.target?.value)}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Input
                label="Priorité"
                type="number"
                placeholder="1"
                value={formData?.priority}
                onChange={(e) => handleInputChange('priority', e?.target?.value)}
                description="1 = Haute priorité, 3 = Basse priorité"
              />

              <div className="flex items-center gap-2 pt-8">
                <Checkbox
                  checked={formData?.isActive}
                  onChange={(checked) => handleInputChange('isActive', checked)}
                />
                <label className="text-sm font-medium text-foreground">Zone active</label>
              </div>
            </div>

            <Input
              label="Notes"
              type="text"
              placeholder="Informations complémentaires..."
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
            />

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={loading}
              >
                {editingZone ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Zone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Code postal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Commune</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Coordonnées</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Rayon</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Priorité</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && zones?.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-muted-foreground">
                    Chargement...
                  </td>
                </tr>
              ) : zones?.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-muted-foreground">
                    Aucune zone configurée
                  </td>
                </tr>
              ) : (
                zones?.map((zone) => (
                  <tr key={zone?.id} className="hover:bg-muted/10">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-foreground">{zone?.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-muted-foreground">{zone?.postalCode || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-muted-foreground">{zone?.municipalityName || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-muted-foreground">
                        {zone?.latitude && zone?.longitude
                          ? `${zone?.latitude?.toFixed(4)}, ${zone?.longitude?.toFixed(4)}`
                          : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-muted-foreground">{zone?.radiusKm} km</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-muted-foreground">{zone?.priority}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(zone?.id, zone?.isActive)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          zone?.isActive
                            ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                        }`}
                      >
                        {zone?.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(zone)}
                          className="text-primary hover:text-primary/80"
                        >
                          <Icon name="Edit" size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(zone?.id)}
                          className="text-error hover:text-error/80"
                        >
                          <Icon name="Trash2" size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ServiceZoneManager;